import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

type LandguageContextProps = {
  children: React.ReactNode;
};

type LanguageContextType = {
  lang?: LanguageType;
  languages: LanguageType[];
  setLang: (newLang?: LanguageType) => void;
};

const languages: LanguageType[] = ['EN', 'DE'];

const LanguageContext = React.createContext<LanguageContextType>({
  languages: [],
  setLang: () => {},
});

const isPresetLanguage = (lang: string): lang is LanguageType => {
  return languages.includes(lang as LanguageType);
};

const LanguageContextProvider: React.FC<LandguageContextProps> = ({
  children,
}) => {
  const { i18n } = useTranslation();

  const [lang, setLang] = useState(
    isPresetLanguage(i18n.language) ? i18n.language : 'EN'
  );

  const handleLanguageChange = useCallback(
    (language?: LanguageType) => {
      const newLanguage = language ?? languages[0];

      setLang(newLanguage);
      i18n.changeLanguage(newLanguage);
    },
    [i18n]
  );

  useEffect(() => {
    if (languages.some(o => o === lang)) return;

    const defaultLanguage = languages[0];
    if (!defaultLanguage) return;

    handleLanguageChange(defaultLanguage);
  }, [lang, handleLanguageChange]);

  const value = useMemo(
    () => ({ lang, languages, setLang: handleLanguageChange }),
    [lang, handleLanguageChange]
  );

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguageContext = () => useContext(LanguageContext);
export default React.memo(
  LanguageContextProvider
) as typeof LanguageContextProvider;
